/**
 * Modal do benefício com o HISTÓRICO DE ALTERAÇÕES (rodada 03/09/2026, pedido
 * do dono): ao selecionar o benefício, a linha do tempo completa da cadeia —
 * cada vigência com seus valores e "o que mudou aqui" (diff × a anterior, do
 * helper puro beneficios-historico) — e as ações de cadastro de "quebras":
 * "Adicionar quebra" (a mudança de valores a partir de um mês = o editor de
 * vínculo existente, que já cria a vigência nova) e "Encerrar a partir de…"
 * (o "abril cancela"). NÃO faz fetch — usa a cadeia já carregada pela aba.
 */
import { Badge, Button, Group, Modal, Text, Timeline } from "@mantine/core";
import { IconPlus } from "@tabler/icons-react";
import { fmtCompetencia } from "../../format";
import {
  detalheDesconto,
  detalheVinculo,
  diffVigencia,
  statusVinculo,
  type GrupoBeneficio,
  type Vinculo,
} from "../../beneficios-historico";

export function ModalHistoricoBeneficio({
  grupo,
  compRef,
  onFechar,
  onAdicionarQuebra,
  onEncerrar,
  onCorrigir,
}: {
  grupo: GrupoBeneficio;
  /** Data-base do status em destaque (a da aba em "Por mês"; a atual na visão geral). */
  compRef: number;
  onFechar: () => void;
  onAdicionarQuebra: (vinculo: Vinculo) => void;
  onEncerrar: (vinculo: Vinculo) => void;
  /** Correção de histórico (23/09/2026): edita uma vigência ENCERRADA in place
   *  (o editor mostra o alerta amarelo e exige a confirmação). */
  onCorrigir?: (vinculo: Vinculo) => void;
}) {
  const { principal } = grupo;
  const st = statusVinculo(principal, compRef);
  // mais recente primeiro (padrão do card); o diff de cada uma é × a ANTERIOR
  // cronológica — na ordem invertida, a que vem DEPOIS
  const linhaDoTempo = [...grupo.cadeia].reverse();
  const temNaoEncerrado = grupo.cadeia.some((v) => v.vigenciaFim === null);
  return (
    <Modal
      opened
      onClose={onFechar}
      title={
        <Group gap="sm" wrap="nowrap">
          <span>Histórico — {grupo.tipoNome}</span>
          <Badge color={st.cor}>{st.rotulo}</Badge>
        </Group>
      }
      size="lg"
      centered
    >
      <Text size="xs" c="dimmed" mb="sm">
        Cada <strong>quebra</strong> é uma <strong>vigência nova</strong> — os meses já fechados nunca mudam. Em cada
        item da linha do tempo, "o que mudou" é o diff contra a vigência anterior.
      </Text>
      <Timeline bulletSize={14} lineWidth={1} mb="md">
        {linhaDoTempo.map((v, i) => {
          const anterior = linhaDoTempo[i + 1] ?? null;
          const sti = statusVinculo(v, compRef);
          const alteracoes = [...diffVigencia(v, anterior)];
          if (v.vigenciaFim !== null) alteracoes.push(`encerrado em ${fmtCompetencia(v.vigenciaFim)}`);
          return (
            <Timeline.Item
              key={v.id}
              title={
                <Text size="xs">
                  {fmtCompetencia(v.vigenciaInicio)} → {v.vigenciaFim ? fmtCompetencia(v.vigenciaFim) : "em vigor"}
                  <Badge ml={6} size="xs" color={sti.cor}>
                    {sti.rotulo}
                  </Badge>
                  {v.id === principal.id && (
                    <Badge ml={4} size="xs" variant="outline" color="dark">
                      na data-base
                    </Badge>
                  )}
                </Text>
              }
            >
              <Text size="xs">
                {detalheVinculo(v)}
                {detalheDesconto(v)}
              </Text>
              {v.observacao && (
                <Text size="xs" c="dimmed">
                  {v.observacao}
                </Text>
              )}
              {alteracoes.map((a, j) => (
                <Text key={j} size="xs" c="dimmed">
                  · {a}
                </Text>
              ))}
              {onCorrigir && v.vigenciaFim !== null && (
                <Button
                  variant="subtle"
                  size="compact-xs"
                  mt={2}
                  onClick={() => onCorrigir(v)}
                  title="Corrigir valores desta vigência encerrada — aplica no histórico (meses fechados têm o snapshot das runs)"
                >
                  corrigir este histórico
                </Button>
              )}
            </Timeline.Item>
          );
        })}
      </Timeline>
      <Group justify="flex-end">
        <Button variant="default" onClick={onFechar}>
          Fechar
        </Button>
        {temNaoEncerrado && (
          <>
            <Button variant="light" leftSection={<IconPlus size={14} />} onClick={() => onAdicionarQuebra(principal)}>
              Adicionar quebra
            </Button>
            <Button variant="subtle" color="yellow.8" onClick={() => onEncerrar(principal)}>
              Encerrar a partir de…
            </Button>
          </>
        )}
      </Group>
    </Modal>
  );
}
